import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { authHeaders } from "@/lib/session";
import { useUser } from "@/store/user";

const STATUS_LABELS = {
  new: { label: "جديد", color: "#ff7aa8" },
  read: { label: "مقروء", color: "#8b6df0" },
  contacted: { label: "تمّ التواصل", color: "#f5c87a" },
  done: { label: "مكتمل", color: "#2ec48e" },
  archived: { label: "مؤرشف", color: "#7a829c" },
};
const STATUS_ORDER = ["new", "read", "contacted", "done", "archived"];

const fetchAuthed = (path, init = {}) =>
  fetch(`${API}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(init.headers || {}) },
  });

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthed, bootLoading } = useUser();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);

  // Verify admin
  useEffect(() => {
    if (bootLoading) return;
    (async () => {
      try {
        const r = await fetchAuthed("/admin/me");
        const data = await r.json();
        setIsAdmin(!!data.is_admin);
      } catch {
        setIsAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [bootLoading, isAuthed]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (q.trim()) params.set("q", q.trim());
      const [rParts, rOver] = await Promise.all([
        fetchAuthed(`/admin/partnerships?${params.toString()}`),
        fetchAuthed(`/admin/overview`),
      ]);
      if (!rParts.ok) {
        const d = await rParts.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${rParts.status}`);
      }
      const data = await rParts.json();
      setItems(data.items || []);
      setCounts(data.counts || {});
      if (rOver.ok) {
        setOverview(await rOver.json());
      }
    } catch (e) {
      setErr(e.message || "تعذّر التحميل");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const updateStatus = async (id, status) => {
    try {
      const r = await fetchAuthed(`/admin/partnerships/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("فشل التحديث");
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status } : x));
      // refresh counts
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("هل أنت متأكّد من حذف هذا الطلب نهائيًّا؟")) return;
    try {
      const r = await fetchAuthed(`/admin/partnerships/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("فشل الحذف");
      setItems((arr) => arr.filter((x) => x.id !== id));
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------- Render guards ----------
  if (bootLoading || !authChecked) {
    return (
      <div className="shell has-bottom-nav admin-page" data-testid="admin-loading">
        <div className="admin-empty">جارٍ التحقّق من الصلاحيّات…</div>
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="shell has-bottom-nav admin-page" data-testid="admin-need-login">
        <div className="admin-gate">
          <div className="admin-gate-icon" aria-hidden>🔐</div>
          <h2>لوحة الإدارة محميّة</h2>
          <p>يُرجى تسجيل الدخول بحساب جوجل الإداريّ للوصول.</p>
          <button className="btn btn-gold" onClick={() => navigate("/login")}
                  data-testid="admin-login-btn">
            تسجيل الدخول
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="shell has-bottom-nav admin-page" data-testid="admin-forbidden">
        <div className="admin-gate">
          <div className="admin-gate-icon" aria-hidden>⛔</div>
          <h2>الوصول مرفوض</h2>
          <p>
            الحساب الحاليّ ({user?.email}) ليس حسابًا إداريًّا.<br />
            هذه الصفحة محصورة بحسب بريد الإدارة المُعتمَد.
          </p>
          <button className="btn btn-ghost" onClick={() => navigate("/")}>
            العودة إلى الرئيسيّة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell has-bottom-nav admin-page" data-testid="admin-page">
      <div className="page-header">
        <h1 data-testid="admin-title">
          لوحة <span className="gold">الإدارة</span>
        </h1>
        <p>إدارة طلبات الشراكة والاستثمار والتعاون الواردة من صفحة المشروع.</p>
      </div>

      {/* Overview cards — headline platform metrics */}
      <div className="admin-overview" data-testid="admin-overview">
        <div className="admin-overview-card" data-testid="ov-total-users">
          <div className="admin-overview-icon" aria-hidden>👥</div>
          <div className="admin-overview-body">
            <div className="admin-overview-num" data-testid="ov-total-users-num">
              {overview?.total_users ?? "—"}
            </div>
            <div className="admin-overview-lbl">إجماليّ المستخدمين</div>
          </div>
        </div>
        <div className="admin-overview-card is-gold" data-testid="ov-active-subs">
          <div className="admin-overview-icon" aria-hidden>💎</div>
          <div className="admin-overview-body">
            <div className="admin-overview-num" data-testid="ov-active-subs-num">
              {overview?.active_subscribers ?? "—"}
            </div>
            <div className="admin-overview-lbl">المشتركون النّشطون</div>
          </div>
        </div>
        <div className="admin-overview-card" data-testid="ov-partnerships">
          <div className="admin-overview-icon" aria-hidden>🤝</div>
          <div className="admin-overview-body">
            <div className="admin-overview-num" data-testid="ov-partnerships-num">
              {overview?.partnership_requests ?? "—"}
            </div>
            <div className="admin-overview-lbl">طلبات الشّراكة</div>
          </div>
        </div>
        <div className="admin-overview-card" data-testid="ov-certificates">
          <div className="admin-overview-icon" aria-hidden>🎓</div>
          <div className="admin-overview-body">
            <div className="admin-overview-num" data-testid="ov-certificates-num">
              {overview?.certificates_issued ?? "—"}
            </div>
            <div className="admin-overview-lbl">الشّهادات المُصدَرة</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-stats" data-testid="admin-stats">
        <button className={`admin-stat ${filter === "all" ? "active" : ""}`}
                onClick={() => setFilter("all")}
                data-testid="stat-all">
          <div className="num">{counts.total ?? 0}</div>
          <div>الإجماليّ</div>
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            className={`admin-stat ${filter === s ? "active" : ""}`}
            onClick={() => setFilter(s)}
            data-testid={`stat-${s}`}
            style={{ "--accent": STATUS_LABELS[s].color }}
          >
            <div className="num">{counts[s] ?? 0}</div>
            <div>{STATUS_LABELS[s].label}</div>
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div className="admin-toolbar">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍 ابحث بالاسم، البريد، الجهة، أو الرسالة..."
          className="admin-search"
          data-testid="admin-search"
        />
        <button className="btn btn-ghost" onClick={load} data-testid="admin-refresh">
          🔄 تحديث
        </button>
      </div>

      {err && (
        <div className="admin-error" data-testid="admin-error">⚠️ {err}</div>
      )}

      {loading ? (
        <div className="admin-empty" data-testid="admin-loading-data">
          جارٍ تحميل الطلبات…
        </div>
      ) : items.length === 0 ? (
        <div className="admin-empty" data-testid="admin-empty">
          <div className="admin-empty-icon" aria-hidden>📭</div>
          <p>
            {filter === "all"
              ? "لا توجد طلبات بعد. شارك صفحة /about مع الجهات للحصول على طلبات."
              : `لا توجد طلباتٌ بحالة «${STATUS_LABELS[filter]?.label || filter}».`}
          </p>
        </div>
      ) : (
        <div className="admin-list" data-testid="admin-list">
          {items.map((req) => {
            const isOpen = expanded === req.id;
            const st = STATUS_LABELS[req.status] || STATUS_LABELS.new;
            return (
              <article
                key={req.id}
                className={`admin-card status-${req.status}`}
                data-testid={`req-${req.id}`}
              >
                <header
                  className="admin-card-head"
                  onClick={() => {
                    setExpanded(isOpen ? null : req.id);
                    if (req.status === "new") updateStatus(req.id, "read");
                  }}
                >
                  <div className="admin-card-main">
                    <div className="admin-card-name">{req.name}</div>
                    <div className="admin-card-meta">
                      <span className="org" dir="auto">
                        {req.organization || "— بدون جهة —"}
                      </span>
                      <span className="sep">·</span>
                      <span className="type">{req.partnership_type || "—"}</span>
                    </div>
                  </div>
                  <div className="admin-card-right">
                    <span
                      className="status-pill"
                      style={{ color: st.color, borderColor: st.color, background: `${st.color}1f` }}
                      data-testid={`status-${req.id}`}
                    >
                      {st.label}
                    </span>
                    <span className="admin-card-date">{fmtDate(req.created_at)}</span>
                    <span className="admin-card-arrow" aria-hidden>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </header>

                {isOpen && (
                  <div className="admin-card-body" data-testid={`body-${req.id}`}>
                    <div className="admin-row">
                      <strong>البريد:</strong>{" "}
                      <a href={`mailto:${req.email}`} dir="ltr"
                         data-testid={`email-${req.id}`}>{req.email}</a>
                    </div>
                    {req.phone && (
                      <div className="admin-row">
                        <strong>الهاتف:</strong>{" "}
                        <a href={`tel:${req.phone}`} dir="ltr">{req.phone}</a>
                      </div>
                    )}
                    <div className="admin-row">
                      <strong>الرسالة:</strong>
                      <p className="admin-message">{req.message}</p>
                    </div>
                    {req.updated_at && (
                      <div className="admin-row muted">
                        آخر تحديث: {fmtDate(req.updated_at)}
                      </div>
                    )}

                    <div className="admin-actions">
                      <select
                        value={req.status}
                        onChange={(e) => updateStatus(req.id, e.target.value)}
                        className="admin-status-select"
                        data-testid={`select-${req.id}`}
                      >
                        {STATUS_ORDER.map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
                        ))}
                      </select>
                      <a
                        className="btn btn-ghost"
                        href={`mailto:${req.email}?subject=${encodeURIComponent(
                          "ردًّا على طلبكم في بَلاغة"
                        )}&body=${encodeURIComponent(
                          `السلام عليكم ${req.name}،\n\nشكرًا لاهتمامكم بمشروع بَلاغة.\n\n`
                        )}`}
                        data-testid={`reply-${req.id}`}
                      >
                        ✉️ ردّ بالبريد
                      </a>
                      <button
                        className="btn btn-danger"
                        onClick={() => remove(req.id)}
                        data-testid={`delete-${req.id}`}
                      >
                        🗑️ حذف
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
